import api, { apiWithRetry } from './api';
import { checkContentLocale, checkContentArrayLocale } from './localeHelper';

/**
 * Conference service for fetching conferences from Strapi API
 */

export const conferenceService = {
  /**
   * Get all conferences
   * @param {Object} options - Query options
   * @param {string} options.sort - Sort order (default: 'startDate:desc')
   * @param {number} options.page - Page number
   * @param {number} options.pageSize - Items per page
   * @param {string} options.populate - Relations to populate (default: '*')
   * @returns {Promise<Object>} Conferences data
   */
  getConferences: async (options = {}) => {
    try {
      const {
        sort = 'startDate:desc',
        page = 1,
        pageSize = 10,
        populate = '*',
      } = options;

      const params = {
        'sort': sort,
        'pagination[page]': page,
        'pagination[pageSize]': pageSize,
        'populate': populate,
      };
      
      const response = await apiWithRetry(() => 
        api.get('/api/conferences', { params })
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching conferences:', error);
      throw new Error(error.userMessage || 'Failed to fetch conferences');
    }
  },

  /**
   * Get upcoming conferences
   * @param {number} limit - Maximum number of conferences to return (default: 5)
   * @returns {Promise<Object>} Upcoming conferences data
   */
  getUpcomingConferences: async (limit = 5) => {
    try {
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      
      const params = {
        'filters[startDate][$gte]': today,
        'sort': 'startDate:asc',
        'pagination[pageSize]': limit,
        'populate': '*',
      };
      
      const response = await apiWithRetry(() => 
        api.get('/api/conferences', { params })
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching upcoming conferences:', error);
      throw new Error(error.userMessage || 'Failed to fetch upcoming conferences');
    }
  },

  /**
   * Get past conferences
   * @param {number} limit - Maximum number of conferences to return (default: 5)
   * @returns {Promise<Object>} Past conferences data
   */
  getPastConferences: async (limit = 5) => {
    try {
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      
      const params = {
        'filters[endDate][$lt]': today,
        'sort': 'startDate:desc',
        'pagination[pageSize]': limit,
        'populate': '*',
      };
      
      const response = await apiWithRetry(() => 
        api.get('/api/conferences', { params })
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching past conferences:', error);
      throw new Error(error.userMessage || 'Failed to fetch past conferences');
    }
  },

  /**
   * Get featured conferences
   * @param {number} limit - Maximum number of conferences to return (default: 5)
   * @returns {Promise<Object>} Featured conferences data
   */
  getFeaturedConferences: async (limit = 5) => {
    try {
      const params = {
        'filters[isFeatured][$eq]': true,
        'sort': 'startDate:desc',
        'pagination[pageSize]': limit,
        'populate': '*',
      };
      
      const response = await apiWithRetry(() => 
        api.get('/api/conferences', { params })
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching featured conferences:', error);
      throw new Error(error.userMessage || 'Failed to fetch featured conferences');
    }
  },

  /**
   * Get conference by ID
   * @param {number} id - Conference ID
   * @param {string} populate - Relations to populate (default: '*')
   * @returns {Promise<Object>} Conference data
   */
  getConferenceById: async (id, populate = '*') => {
    try {
      const params = { populate };
      
      const response = await apiWithRetry(() => 
        api.get(`/api/conferences/${id}`, { params })
      );
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching conference ${id}:`, error);
      throw new Error(error.userMessage || 'Failed to fetch conference');
    }
  },

  /**
   * Get conference by slug
   * @param {string} slug - Conference slug
   * @param {string} populate - Relations to populate (default: '*')
   * @param {string} locale - Optional locale override
   * @returns {Promise<Object>} Conference data
   */
  getConferenceBySlug: async (slug, populate = '*', locale = null) => {
    try {
      const effectiveLocale = locale || null;

      // First, try to find with slug + requested locale
      const params = {
        'filters[slug][$eq]': slug,
        'populate': populate,
      };
      if (effectiveLocale) {
        params.locale = effectiveLocale;
      }
      
      const response = await apiWithRetry(() => 
        api.get('/api/conferences', { params })
      );
      
      if (response.data.data && response.data.data.length > 0) {
        return checkContentLocale(response.data.data[0]);
      }

      // Slug not found in requested locale — try finding it in ANY locale
      // then use its localizations to get the correct locale version
      for (const tryLocale of ['en', 'vi', 'pl']) {
        if (tryLocale === effectiveLocale) continue;
        const searchParams = {
          'filters[slug][$eq]': slug,
          'populate[localizations][populate]': '*',
          'populate[featuredImage]': '*',
          'populate[thumbnailImage]': '*',
          'locale': tryLocale,
        };
        const searchResponse = await apiWithRetry(() => 
          api.get('/api/conferences', { params: searchParams })
        );
        if (searchResponse.data.data && searchResponse.data.data.length > 0) {
          const foundEntry = searchResponse.data.data[0];
          
          // If the found entry is already in the requested locale, return it
          if (!effectiveLocale || foundEntry.attributes.locale === effectiveLocale) {
            return checkContentLocale(foundEntry);
          }

          // Check its localizations for the requested locale
          const localizations = foundEntry.attributes.localizations?.data || [];
          const localeMatch = localizations.find(
            (loc) => loc.attributes.locale === effectiveLocale
          );
          
          if (localeMatch) {
            // Fetch the full entry for the matched locale by its ID
            const fullEntry = await apiWithRetry(() =>
              api.get(`/api/conferences/${localeMatch.id}`, { params: { populate } })
            );
            if (fullEntry.data.data) {
              return checkContentLocale(fullEntry.data.data);
            }
          }

          // No localization found for requested locale — return what we found
          return checkContentLocale(foundEntry);
        }
      }

      throw new Error('Conference not found');
    } catch (error) {
      console.error(`Error fetching conference by slug ${slug}:`, error);
      throw new Error(error.userMessage || 'Failed to fetch conference');
    }
  },

  /**
   * Get important dates for a conference
   * @param {string} conferenceSlug - Conference slug
   * @returns {Promise<Object>} Important dates data
   */
  getConferenceImportantDates: async (conferenceSlug) => {
    try {
      const params = {
        'filters[relatedConference][slug][$eq]': conferenceSlug,
        'sort': 'date:asc',
        'populate': '*',
      };
      
      const response = await apiWithRetry(() => 
        api.get('/api/important-dates', { params })
      );
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching important dates for conference ${conferenceSlug}:`, error);
      throw new Error(error.userMessage || 'Failed to fetch important dates');
    }
  },
};

export default conferenceService;
